import React, { FC, useEffect, useRef, useState } from 'react';
import { CircularProgress } from '@mui/material';
import Cookies from 'js-cookie';
import { enqueueSnackbar } from 'notistack';
import useLocales from 'hooks/useLocales';
import Button from 'components/Button/Index';
import { MatchProfileService } from 'services/matchProfile/matchProfile';
import { IMatchProfile } from 'core/interface/matchProfile';
import NoData from 'components/shared/NoData/Index';
import MatchingProfileBlock from 'components/MatchingProfileBlock/Index';
import Popup from 'components/shared/Popups/Popup/Index';
import { JobService } from 'services/jobs/job';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import CompetenciesSelect from 'components/shared/CompetenciesSelect';
import { useNavigate } from 'react-router';
import CompetenciesPopup from 'components/shared/Popups/CompetenciesPopup/Index';
import { getCompetenciesSelectFooter } from 'components/shared/CompetenciesSelect';
import Pagination from '@material-ui/core/Pagination';
import { throwIfAIResponseNotOk } from 'services/ai/ai';
import { useAIOperationCosts } from 'hooks/useAIOperationCosts';
import { useAICreditAvailability } from 'hooks/useAICreditAvailability';

import './Index.scss';

const MatchingProfile: FC = (): JSX.Element => {
  const { t } = useLocales();
  const { jobCompetenciesCost } = useAIOperationCosts();
  const aiCompetenciesCredits = useAICreditAvailability(jobCompetenciesCost);
  const [editPopUp, setEditPopUp] = useState<boolean>(false);
  const [selectedLabels, setSelectedLabels] = useState<string[]>([]);
  const [ids, setIds] = useState<string[]>([]);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [selectedMatchProfileId, setSelectedMatchProfileId] = useState<any>();
  const [selectedMatchProfile, setSelectedMatchProfile] = useState<any>();
  const [step, setStep] = useState(1);
  const [scores, setScores] = useState<{ [label: string]: number }>({});
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(6);
  const [offset, setOffset] = useState(0);
  const [totalCount, setTotalCount] = useState(0);

  const [formValues, setFormValues] = useState({
    matching: '',
    culture_profile: ''
  });
  const { assessmentByGroup } = useSelector(
    (state: RootState) => state.assessment
  );

  const { matchProfile }: any = useSelector((state: RootState) => state.match);
  const [loading, setLoading] = useState<boolean>(true);
  const [isExtractingCompetencies, setIsExtractingCompetencies] =
    useState<boolean>(false);
  const competencyAbortControllerRef = useRef<AbortController | null>(null);

  const getApplications = () => {
    JobService.getAssessmentByGroup();
  };

  const getSearchProfile = () => {
    MatchProfileService.getMatchProfilesSearch(query);
  };

  useEffect(() => {
    if (selectedMatchProfile) {
      setFormValues({
        matching: selectedMatchProfile?.title,
        culture_profile: selectedMatchProfile?.culture_profile
      });
      setSelectedLabels(selectedMatchProfile?.competency_labels);
      const a = selectedMatchProfile?.competencies?.map(
        (e: any) => e?.competency?.id
      );
      setIds(a);
    }
  }, [selectedMatchProfile]);

  useEffect(() => {
    getSearchProfile();
    getMatchProfiles();
    getApplications();
  }, []);

  useEffect(() => {
    getMatchProfiles();
  }, [limit, offset]);

  useEffect(() => {
    setOffset((page - 1) * limit);
  }, [page, limit]);

  useEffect(
    () => () => {
      competencyAbortControllerRef.current?.abort();
    },
    []
  );

  useEffect(() => {
    const selectedProfile = matchProfile?.results?.find(
      (profile: any) => profile.id === selectedMatchProfileId
    );

    if (selectedProfile) {
      setSelectedMatchProfile(selectedProfile);
    }
  }, [matchProfile, selectedMatchProfileId]);

  useEffect(() => {
    if (matchProfile && matchProfile.count !== undefined) {
      setTotalCount(matchProfile.count);
    } else if (matchProfile && matchProfile.results) {
      setTotalCount(matchProfile.results.length);
    }
  }, [matchProfile]);

  useEffect(() => {
    setPage(1);
  }, [query]);

  const getMatchProfiles = async (): Promise<void> => {
    try {
      setLoading(true);
      await MatchProfileService.getMatchProfiles(false, limit, offset);
      setLoading(false);
    } catch (error) {
      console.log(error);
      setLoading(false);
    }
  };

  const handleDelete = async (id: number): Promise<void> => {
    try {
      await MatchProfileService.deleteMatchProfile(id, false);
    } catch (error) {
      console.log(error);
    }
  };

  const handleClosePopup = (): void => {
    competencyAbortControllerRef.current?.abort();
    competencyAbortControllerRef.current = null;
    setIsExtractingCompetencies(false);
    setSelectedMatchProfile(null);
    setSelectedMatchProfileId(null);
    setSelectedLabels([]);
    setIds([]);
    setScores({});
    setFormValues({ matching: '', culture_profile: '' });
    setEditPopUp(!editPopUp);
    setStep(1);
  };

  const handleSetSelected = (label: string, id: string | number) => {
    const normalizedId = String(id);
    const labelIndex = selectedLabels.indexOf(label);

    if (labelIndex !== -1) {
      setSelectedLabels(selectedLabels.filter((_, i) => i !== labelIndex));
      setIds(ids.filter((_, i) => i !== labelIndex));
      return;
    }

    if (selectedLabels.length >= 10) {
      return;
    }

    setSelectedLabels([...selectedLabels, label]);
    setIds([...ids, normalizedId]);
  };

  const handleNext = () => {
    if (
      selectedLabels.length >= 7 &&
      selectedLabels.length <= 10 &&
      formValues.matching &&
      formValues.culture_profile
    ) {
      const idLabelArray = ids.map((id, index) => ({
        id,
        competency: selectedLabels[index]
      }));
      const existingLabels = selectedMatchProfile?.competency_labels || [];
      const competencyScores =
        selectedMatchProfile?.competency_min_scores || [];

      const data = {
        name: formValues.matching,
        culture_profile: formValues.culture_profile,
        competencies: idLabelArray,
        is_culture: false,
        edit: selectedMatchProfile ? selectedMatchProfile?.id : false,
        scores: idLabelArray.map(({ competency }) =>
          existingLabels.includes(competency)
            ? competencyScores[existingLabels.indexOf(competency)]
            : 0
        )
      };

      localStorage.setItem('selected', JSON.stringify(data));
      navigate('/company/culture');
    }
  };

  const gerSearch = (id: number) => {
    navigate(`/company/find-candidates/${id}/find`);
  };

  const getJobList = (id: number) => {
    navigate(`/company/find-candidates/${id}/requests`);
  };

  const mapCompetencyIds = (
    competencyIds: number[]
  ): { ids: string[]; labels: string[] } => {
    const allCompetencies = assessmentByGroup.flatMap(
      (group: { competencies: { id: number; name: string }[] }) =>
        group.competencies || []
    );
    const labels: string[] = [];
    const competencyIdsAsStrings: string[] = [];

    competencyIds.forEach((id) => {
      const competency = allCompetencies.find(
        (item: { id: number; name: string }) => item.id === id
      );
      if (competency && labels.length < 10) {
        labels.push(competency.name);
        competencyIdsAsStrings.push(String(competency.id));
      }
    });

    return { ids: competencyIdsAsStrings, labels };
  };

  const extractCompetencies = async (): Promise<void> => {
    const profileTitle = formValues.matching?.trim();
    if (!profileTitle || isExtractingCompetencies) {
      return;
    }

    try {
      setIsExtractingCompetencies(true);
      competencyAbortControllerRef.current?.abort();
      competencyAbortControllerRef.current = new AbortController();

      const accessToken = Cookies.get('accesstoken');
      const response = await fetch('/api/ai/extract_competencies/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken && { Authorization: `Bearer ${accessToken}` })
        },
        body: JSON.stringify({ job_title: profileTitle }),
        signal: competencyAbortControllerRef.current.signal
      });

      await throwIfAIResponseNotOk(response);

      const res = await response.json();
      const competencyIds: number[] =
        res.Competencies || res.competencies || [];

      const aiCompetencies = mapCompetencyIds(competencyIds);
      if (!aiCompetencies.labels.length) {
        enqueueSnackbar(
          t('We could not suggest competencies right now. Please select them manually.'),
          { variant: 'warning' }
        );
        return;
      }

      setSelectedLabels(aiCompetencies.labels);
      setIds(aiCompetencies.ids);
      setScores(
        aiCompetencies.labels.reduce(
          (acc, label) => ({ ...acc, [label]: scores[label] ?? 5 }),
          {}
        )
      );
    } catch (error: any) {
      if (error?.name === 'AbortError') {
        return;
      }
      console.error('Error extracting competencies:', error);
      enqueueSnackbar(error?.message || t('Failed to extract competencies'), {
        variant: 'error'
      });
    } finally {
      setIsExtractingCompetencies(false);
      competencyAbortControllerRef.current = null;
    }
  };

  const handleSavePopup = async (
    selectedLabels: string[],
    scores: { [label: string]: number },
    formValues: any
  ): Promise<void> => {
    // Build reqBody for match profile
    const allCompetencies = assessmentByGroup.flatMap(
      (group: any) => group.competencies
    );
    const nameToId: { [name: string]: number } = {};
    allCompetencies.forEach((c: any) => {
      nameToId[c.name] = c.id;
    });

    const reqBody = {
      title: formValues.matching,
      culture_profile:
        formValues.culture_profile?.id || formValues.culture_profile,
      is_culture: false,
      competencies: selectedLabels.map((label) => ({
        competency: nameToId[label],
        min_score: scores[label] ?? 5
      }))
    };

    if (selectedMatchProfile && selectedMatchProfile.id) {
      await MatchProfileService.editMatchProfile(
        selectedMatchProfile.id,
        false,
        reqBody
      );
    } else {
      await MatchProfileService.createMatchProfile(false, reqBody);
    }
    await getMatchProfiles();
    setEditPopUp(false);
    setStep(1);
  };

  return (
    <div
      className="matching"
      style={{ flex: 1, display: 'flex', flexDirection: 'column' }}
    >
      <div className="matching__header">
        <span className="matching__header-title">{t('Matching Profile')}</span>
        <Button
          t={t}
          title={t('Create New Match Profile')}
          className="matching__header-btn"
          onClick={() => setEditPopUp(true)}
        />
      </div>
      {loading ? (
        <div className="loadingContainer">
          <CircularProgress className="loading" />
        </div>
      ) : matchProfile?.results?.length === 0 ? (
        <NoData />
      ) : (
        <>
          <div className="match-blocks">
            {matchProfile?.results?.map((profile: any) => (
              <MatchingProfileBlock
                id={profile.id}
                title={profile.title}
                name={''}
                date={profile.last_competencies_updated_on}
                key={profile.id}
                setEditPopUp={setEditPopUp}
                setDeletedItem={handleDelete}
                setSelectedMatchProfileId={setSelectedMatchProfileId}
                getOnePost={getJobList}
                getList={gerSearch}
                type="find-candidate"
                createdBy={profile?.user || ''}
              />
            ))}
          </div>
          <div
            style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}
          >
            <Pagination
              className="pagination"
              count={Math.ceil((matchProfile?.count || 0) / limit)}
              page={page}
              onChange={(_, value) => setPage(value)}
              variant="outlined"
              shape="rounded"
              showFirstButton
              showLastButton
            />
          </div>
        </>
      )}
      {editPopUp && (
        <CompetenciesPopup
          className="popup-add-new"
          title={t('Please select the most important competencies for your job opening.')}
          description="We advise you to select no more than 10 competencies. And you should only go for the once that really matter to achieve success in the work the candidate needs to execute. The average is 7 competencies per matching profile. But depending on your results you can always add or delete competencies. This way you can fine-tune your ideal profile."
          onClose={handleClosePopup}
          footer={getCompetenciesSelectFooter({
            step,
            t,
            handleBack: () => setStep(1),
            handleSave: () =>
              handleSavePopup(selectedLabels, scores, formValues),
            handleNext: () => setStep(2),
            selectedList: selectedLabels,
            disabled:
              selectedLabels.length < 7 ||
              selectedLabels.length > 10 ||
              !formValues.matching ||
              !formValues.culture_profile
          })}
        >
          <CompetenciesSelect
            step={step}
            setStep={setStep}
            setQuery={setQuery}
            competencies={assessmentByGroup}
            selectedList={selectedLabels}
            onSelect={handleSetSelected}
            matchProfile={true}
            formValues={formValues}
            setFormValues={setFormValues}
            scores={scores}
            setScores={setScores}
            onGenerateFromTitle={extractCompetencies}
            isGeneratingFromTitle={isExtractingCompetencies}
            showAiTitleButton
            aiTitleCreditCost={jobCompetenciesCost}
            isAiTitleCreditLimited={aiCompetenciesCredits.isCreditLimited}
            aiTitleCreditTooltip={
              aiCompetenciesCredits.isCreditLimited
                ? aiCompetenciesCredits.outOfCreditsTooltip
                : undefined
            }
          />
        </CompetenciesPopup>
      )}
    </div>
  );
};

export default MatchingProfile;
